import {
  IFoodItem,
  IFoodMenuCategory,
  IMenuJSON,
  ISubCategoriesJson,
} from "../../../../redux/food/food.types";
import {
  IMasterFoodMenuPayload,
  ILeadFoodMenuPayload,
} from "../types/menuBuilder.types";
import { makeSelectionKey, parseSelectionKey } from "./selectionKey";

/** Placed category UUID from _selectionKey (fallback to category_list[0]) */
const placedCategoryUuid = (item: IFoodItem): string => {
  const key = (item as any)._selectionKey as string | undefined;
  if (key) return parseSelectionKey(key).categoryUuid;
  return item.category_list?.[0]?.category_uuid ?? "";
};

/** Placed subcategory UUID from _selectionKey (fallback to sub_category_uuid) */
const placedSubCategoryUuid = (item: IFoodItem): string | null => {
  const key = (item as any)._selectionKey as string | undefined;
  if (key) return parseSelectionKey(key).subCategoryUuid;
  return item.sub_category_uuid ?? null;
};

export interface IBuildSavePayloadParams {
  mode: "master" | "lead";
  selectedItems: IFoodItem[];
  categoryLimits: Record<string, number>;
  itemVisibility: Record<string, boolean>;
  isFixed: boolean;
  allCategories: IFoodMenuCategory[];
  title: string;
  description: string;
  price: number | null;
  leadUuid: string | null;
  masterMenuUuid: string | null;
  masterMenuName: string | null;
  masterMenuPrice: string | null;
  addedLeadMenuUuid: string | null;
  companyOutletUuid: string | null;
  tags?: string[];
  categoryOrder?: string[];
  taxType?: "GST" | "VAT" | "HST";
}

/**
 * Builds the save payload for either master or lead mode.
 *
 * Constructs menu_json by:
 * 1. Iterating all categories
 * 2. For each category, finding selected items belonging to it
 * 3. Grouping items by subcategory
 * 4. Only including categories that have selected items OR a configured limit > 0
 * 5. Attaching max_selection at both category and subcategory level from categoryLimits
 */
export function buildSavePayload(
  params: IBuildSavePayloadParams
): IMasterFoodMenuPayload | ILeadFoodMenuPayload {
  const {
    mode,
    selectedItems,
    categoryLimits,
    itemVisibility,
    isFixed,
    allCategories,
    title,
    description,
    price,
    leadUuid,
    masterMenuUuid,
    masterMenuName,
    masterMenuPrice,
    addedLeadMenuUuid,
    companyOutletUuid,
    tags,
    categoryOrder,
    taxType,
  } = params;

  let menuJson = buildMenuJson(selectedItems, categoryLimits, itemVisibility, isFixed, allCategories);

  // Sort menu_json by categoryOrder if provided
  if (categoryOrder && categoryOrder.length > 0) {
    menuJson.sort((a, b) => {
      const idxA = categoryOrder.indexOf(a.category_uuid);
      const idxB = categoryOrder.indexOf(b.category_uuid);
      // Categories not in the order go to the end
      return (idxA === -1 ? Infinity : idxA) - (idxB === -1 ? Infinity : idxB);
    });
  }

  if (mode === "master") {
    const payload: IMasterFoodMenuPayload = {
      master_menu_uuid: masterMenuUuid,
      title,
      description,
      price,
      tax_type: taxType ?? "GST",
      is_fixed: isFixed,
      menu_json: menuJson,
      company_outlet_uuid: companyOutletUuid,
      status: "ACTIVE",
      tags: tags ?? [],
    };
    return payload;
  }

  const payload: ILeadFoodMenuPayload = {
    added_lead_menu_uuid: addedLeadMenuUuid,
    leads_uuid: leadUuid,
    title,
    description,
    menu_json: menuJson,
    company_outlet_uuid: companyOutletUuid,
    status: "ACTIVE",
    is_fixed: isFixed ? 1 : 0,
    master_menu_uuid: masterMenuUuid,
    master_menu_name: masterMenuName,
    master_menu_price: masterMenuPrice,
  };
  return payload;
}

/**
 * Builds the menu_json array from selected items, limits, and category data.
 * When isFixed=true, includes ALL items with isVisible flag (not just selected).
 * When isFixed=false, includes only selected items.
 */
function buildMenuJson(
  selectedItems: IFoodItem[],
  categoryLimits: Record<string, number>,
  itemVisibility: Record<string, boolean>,
  isFixed: boolean,
  allCategories: IFoodMenuCategory[]
): IMenuJSON[] {
  const menuJson: IMenuJSON[] = [];
  // Build composite key set so the same item in different category/sub slots
  // is treated as distinct selections (matches the ADD_ITEM stamp in context)
  const selectedKeys = new Set(
    selectedItems.map(
      (i) =>
        (i as any)._selectionKey ??
        makeSelectionKey(
          i.category_list?.[0]?.category_uuid ?? "",
          i.sub_category_uuid,
          i.food_item_uuid ?? "",
        ),
    ),
  );

  for (const category of allCategories) {
    const categoryUuid = category.category_uuid ?? "";

    // Find selected items PLACED into this category (from _selectionKey, not DB membership)
    const categorySelectedItems = selectedItems.filter(
      (item) => placedCategoryUuid(item) === categoryUuid
    );

    // Get the category limit from categoryLimits
    const categoryLimit = categoryLimits[categoryUuid] ?? 0;

    if (isFixed) {
      // Fixed mode: include ALL items from this category (from Redux data) with isVisible + isSelected flags
      const allCategoryItems: IFoodItem[] = [];

      // Items from category.food_items
      if (category.food_items) {
        for (const item of category.food_items) {
          const uuid = item.food_item_uuid ?? "";
          // isSelected: check composite key — no sub for category-level items
          const itemKey = makeSelectionKey(categoryUuid, null, uuid);
          allCategoryItems.push({
            ...item,
            isVisible: itemVisibility[uuid] !== false,
            isSelected: selectedKeys.has(itemKey),
            category_list: item.category_list?.length ? item.category_list : [{
              category_uuid: categoryUuid,
              category_name: category.category_name,
              category_description: "",
              company_outlet_uuid: "",
              status: "ACTIVE",
              food_items: [],
              subcategories: [],
            }],
          } as any);
        }
      }

      // Items from subcategories
      if (category.subcategories) {
        for (const sub of category.subcategories) {
          if (sub.food_items) {
            for (const item of sub.food_items) {
              // Avoid duplicates within this category's flat list
              if (allCategoryItems.some((i) => i.food_item_uuid === item.food_item_uuid && (i as any).sub_category_uuid === sub.food_subcategory_name_uuid)) continue;
              const uuid = item.food_item_uuid ?? "";
              const subUuid = sub.food_subcategory_name_uuid ?? "";
              const itemKey = makeSelectionKey(categoryUuid, subUuid, uuid);
              allCategoryItems.push({
                ...item,
                sub_category_uuid: sub.food_subcategory_name_uuid,
                isVisible: itemVisibility[uuid] !== false,
                isSelected: selectedKeys.has(itemKey),
                category_list: item.category_list?.length ? item.category_list : [{
                  category_uuid: categoryUuid,
                  category_name: category.category_name,
                  category_description: "",
                  company_outlet_uuid: "",
                  status: "ACTIVE",
                  food_items: [],
                  subcategories: [],
                }],
              } as any);
            }
          }
        }
      }

      // Only include if there are items or a limit
      if (allCategoryItems.length === 0 && categoryLimit <= 0) continue;

      const subcategories = buildSubcategories(
        allCategoryItems,
        category.subcategories,
        categoryLimits
      );

      menuJson.push({
        category_uuid: categoryUuid,
        category_name: category.category_name,
        food_items: allCategoryItems,
        subcategories,
        max_selection: categoryLimit > 0 ? categoryLimit : undefined,
      });
    } else {
      // Normal mode: only include selected items
      const taggedItems = categorySelectedItems.map((item) => ({
        ...item,
        isVisible: itemVisibility[item.food_item_uuid ?? ""] !== false,
      }));

      if (taggedItems.length === 0 && categoryLimit <= 0) continue;

      const subcategories = buildSubcategories(
        taggedItems,
        category.subcategories,
        categoryLimits
      );

      menuJson.push({
        category_uuid: categoryUuid,
        category_name: category.category_name,
        food_items: taggedItems,
        subcategories,
        max_selection: categoryLimit > 0 ? categoryLimit : undefined,
      });
    }
  }

  return menuJson;
}

/**
 * Builds subcategory structures for a category, attaching limits only (no items).
 * Items are stored at the category level in food_items, not duplicated in subcategories.
 */
function buildSubcategories(
  categoryItems: IFoodItem[],
  subcategories: ISubCategoriesJson[],
  categoryLimits: Record<string, number>
): ISubCategoriesJson[] {
  const result: ISubCategoriesJson[] = [];

  for (const subcategory of subcategories) {
    const subcategoryUuid = subcategory.food_subcategory_name_uuid ?? "";

    // Check if any items are PLACED into this subcategory (from _selectionKey)
    const hasItems = categoryItems.some(
      (item) => placedSubCategoryUuid(item) === subcategoryUuid
    );

    // Get the subcategory limit from categoryLimits (same map, keyed by UUID)
    const subcategoryLimit = categoryLimits[subcategoryUuid] ?? 0;

    // Only include subcategories with items OR a configured limit > 0
    if (!hasItems && subcategoryLimit <= 0) {
      continue;
    }

    const subEntry: ISubCategoriesJson = {
      food_subcategory_name_uuid: subcategoryUuid,
      subcategory_name: subcategory.subcategory_name,
      food_items: [],  // Items stored at category level, not here
      max_selection: subcategoryLimit > 0 ? subcategoryLimit : undefined,
      category_uuid: subcategory.category_uuid,
    };

    result.push(subEntry);
  }

  return result;
}
